import { Tabs, toastError } from 'admination-react'
import { useApp } from '@/core/state/appStore'
import { useOpenPersonOverview } from '@/core/hooks/usePhaseNav'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatZeitpunkt } from '@/core/lib/dates'
import {
  Button,
  Callout,
  DocumentFields,
  FormScroll,
  FormSection,
  Icon,
  ItemNumber,
  ModalHead,
  Panel,
  Paragraph,
  Pill,
  StatusLine,
  Title,
  Wizard,
  WizardPage,
} from 'admination-react'
import { blattKonflikt, blattPdf, documentBlob } from '@/core/api/documents'
import { ApiError } from '@/core/api/http'
import { PdfRegion, ZOOMED_OUT_PDF_SCALE } from '@/core/components/modal/PdfRegion'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import { downloadBlob } from '@/core/lib/files'
import { pngBase64, type PortalBlatt } from '../../../api/portal'
import {
  GEGENZEICHNUNG,
  gegenzeichnen,
  isGegenzeichnungBlatt,
  type GegenzeichnungBlatt,
} from '../../../api/teilnehmerakte-staff'
import { BLATT_IDS, SHEETS, blattKey, type BlattId } from '../../../lib/teilnehmerakte'
import { sheetAnswers } from '../sheet-view'

export const GEGENZEICHNUNG_BLAETTER: readonly GegenzeichnungBlatt[] = BLATT_IDS.filter(isGegenzeichnungBlatt)

export type StaffAkte = Record<GegenzeichnungBlatt, PortalBlatt>

export function staffAkteFrom(rows: readonly PortalBlatt[]): StaffAkte {
  return Object.fromEntries(GEGENZEICHNUNG_BLAETTER.map((id, i) => [id, rows[i]])) as StaffAkte
}

type SignFailure = 'notSigned' | 'alreadyCountersigned' | 'expired' | 'generic'

const BLATT_NICHT_SIGNIERT = 'blatt_nicht_signiert'
const BLATT_GEGENGEZEICHNET = 'blatt_gegengezeichnet'

function signFailureOf(error: unknown): SignFailure {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      if (error.detailText === BLATT_NICHT_SIGNIERT) return 'notSigned'
      if (error.detailText === BLATT_GEGENGEZEICHNET) return 'alreadyCountersigned'
      return 'generic'
    }
    if (error.status === 403) return 'expired'
  }
  return 'generic'
}

const SIGN_FAILURE_TEXT: Record<SignFailure, string> = {
  notSigned: 'akte.notSignedYet',
  alreadyCountersigned: 'akte.alreadyCountersigned',
  expired: 'akte.countersignExpired',
  generic: 'akte.countersignError',
}

export function SheetPdf({
  dokumentId,
  sheet = false,
  failedText,
  onLoaded,
}: {
  dokumentId: string
  sheet?: boolean
  failedText?: string
  onLoaded?: (pdf: Blob) => void
}) {
  const { t } = useTranslation('vergabeStaff')
  const [blob, setBlob] = useState<Blob | null>(null)
  const [failed, setFailed] = useState<{ text: string; quiet: boolean } | null>(null)
  const failure = failedText ?? t('akte.pdfError')
  const latest = useRef({ failure, onLoaded, t })
  latest.current = { failure, onLoaded, t }

  useEffect(() => {
    let cancelled = false
    const laden = sheet ? blattPdf : documentBlob
    laden(dokumentId).then(
      (pdf) => {
        if (cancelled) return
        setBlob(pdf)
        latest.current.onLoaded?.(pdf)
      },
      (error: unknown) => {
        if (cancelled) return
        const { failure, t } = latest.current
        const konflikt = sheet ? blattKonflikt(error) : null
        if (konflikt === 'nicht_ausgefuellt') {
          setFailed({ text: t('akte.notFilled'), quiet: true })
          return
        }
        const restricted = konflikt === 'eingeschraenkt' || (sheet && error instanceof ApiError && error.status === 404)
        const text = restricted ? t('akte.openRestricted') : failure
        setFailed({ text, quiet: false })
        toastError(text)
      },
    )
    return () => {
      cancelled = true
    }
  }, [dokumentId, sheet])

  if (failed) {
    return (
      <FormScroll>
        <FormSection>
          {failed.quiet ? (
            <StatusLine>{failed.text}</StatusLine>
          ) : (
            <Callout tone="error" role="alert">
              {failed.text}
            </Callout>
          )}
        </FormSection>
      </FormScroll>
    )
  }
  return (
    <PdfRegion
      path={null}
      localBlob={blob}
      waiting={!blob}
      loadingLabel={t('akte.pdfLoading')}
      placeholder={t('akte.pdfError')}
      scaleFactor={ZOOMED_OUT_PDF_SCALE}
      reading
    />
  )
}

function pdfFilename(blatt: BlattId, title: string, participantName: string): string {
  return `${blatt} ${title} - ${participantName}.pdf`.replace(/[\\/:*?"<>|]/g, '')
}

function nextSheet(
  akte: StaffAkte,
  countersigned: ReadonlySet<GegenzeichnungBlatt>,
  after?: GegenzeichnungBlatt,
): GegenzeichnungBlatt | undefined {
  const waiting = (id: GegenzeichnungBlatt) => akte[id].status === 'unterschrieben' && !countersigned.has(id)
  const from = after ? GEGENZEICHNUNG_BLAETTER.indexOf(after) + 1 : 0
  return GEGENZEICHNUNG_BLAETTER.slice(from).find(waiting) ?? GEGENZEICHNUNG_BLAETTER.find(waiting)
}

function allCountersigned(akte: StaffAkte, countersigned: ReadonlySet<GegenzeichnungBlatt>): boolean {
  return GEGENZEICHNUNG_BLAETTER.every((id) => countersigned.has(id) || !!akte[id].gegengezeichnet_am)
}

export function AkteViewer({
  coachingId,
  participantName,
  akte,
  reloading,
  onReload,
  onClose,
  onCountersigned,
}: {
  coachingId: string
  participantName: string
  akte: StaffAkte
  reloading: boolean
  onReload: () => void
  onClose: () => void
  onCountersigned: () => void
}) {
  const { t } = useTranslation('vergabeStaff')
  const { t: tp } = useTranslation('portal')
  const { dispatch } = useApp()
  const openPerson = useOpenPersonOverview()
  const [countersigned, setCountersigned] = useState<ReadonlySet<GegenzeichnungBlatt>>(
    () => new Set(GEGENZEICHNUNG_BLAETTER.filter((id) => !!akte[id].gegengezeichnet_am)),
  )
  const countersignedNow = useRef(countersigned)
  const [current, setCurrent] = useState<GegenzeichnungBlatt>(() => nextSheet(akte, countersigned) ?? GEGENZEICHNUNG_BLAETTER[0])
  const [signing, setSigning] = useState<GegenzeichnungBlatt | null>(null)
  const [pdf, setPdf] = useState<{ dokumentId: string; blob: Blob } | null>(null)

  const sheet = SHEETS[current]
  const blatt = akte[current]
  const status = akte[current].status
  const signed = status === 'unterschrieben'
  const dokumentId = signed ? (blatt.dokument_id ?? null) : null
  const sheetTitle = tp(`blatt.${blattKey(current)}`)

  async function countersign(dataUrl: string) {
    if (!signing) return
    const id = signing
    const markDone = () => {
      countersignedNow.current = new Set(countersignedNow.current).add(id)
      setCountersigned(countersignedNow.current)
    }
    try {
      await gegenzeichnen(coachingId, id, pngBase64(dataUrl))
      markDone()
    } catch (error) {
      const failure = signFailureOf(error)
      toastError(t(SIGN_FAILURE_TEXT[failure]))
      if (failure === 'alreadyCountersigned') markDone()
      throw error
    }
  }

  function padClosed() {
    const id = signing
    const done = countersignedNow.current
    setSigning(null)
    if (!id || !done.has(id)) return
    if (allCountersigned(akte, done)) {
      onCountersigned()
      return
    }
    const next = nextSheet(akte, done, id)
    if (next) setCurrent(next)
  }

  const loadedPdf = dokumentId && pdf?.dokumentId === dokumentId ? pdf.blob : null
  const downloadAction = dokumentId ? (
    <Pill>
      <Button
        disabled={!loadedPdf}
        onClick={() => downloadBlob(loadedPdf, pdfFilename(current, sheetTitle, participantName))}
      >
        <Icon name="i-download" />
        {t('akte.pdfDownload')}
      </Button>
    </Pill>
  ) : null

  const reloadAction = (
    <Pill>
      <Button loading={reloading} onClick={onReload}>
        <Icon name="i-search" />
        {t('akte.sheetsReload')}
      </Button>
    </Pill>
  )
  const isDone = (id: GegenzeichnungBlatt) => countersigned.has(id) || !!akte[id].gegengezeichnet_am
  const done = isDone(current)
  const countersignAction = (
    <Pill>
      <Button
        data-blatt={current}
        primaryAction
        disabled={done || !signed}
        onClick={() => setSigning(current)}
      >
        <Icon name={done ? 'i-check' : 'i-signature'} />
        {done
          ? t('akte.countersigned')
          : t(GEGENZEICHNUNG[current] === 'unterweiser' ? 'akte.unterweiser' : 'akte.traeger')}
      </Button>
    </Pill>
  )
  const countersignHint = !signed && !done ? <Paragraph variant="muted">{t('akte.notSignedYet')}</Paragraph> : null

  const bar = (
    <ModalHead
      left={{ type: 'title', icon: 'i-person', label: participantName, onClick: () => openPerson(coachingId, () => dispatch({ type: 'CLOSE' })) }}
      center={<Tabs items={GEGENZEICHNUNG_BLAETTER.map((id) => ({
        name: id, label: `${id} · ${tp(`blatt.${blattKey(id)}`)}`, completed: isDone(id),
      }))} activeName={current} onChange={(id) => setCurrent(id as typeof current)} />}
      actions={
        <>
          {reloadAction}
          {downloadAction}
          {countersignAction}
        </>
      }
      onClose={onClose}
      closeLabel={t('akte.close')}
    />
  )

  const overlay = (
    <SignaturePad
      open={signing !== null}
      title={signing ? `${signing} · ${tp(`blatt.${blattKey(signing)}`)}` : ''}
      successMessage={t('akte.countersignSuccess')}
      onSubmit={countersign}
      onClose={padClosed}
    />
  )

  const answers = !dokumentId && status !== 'offen' ? sheetAnswers(sheet, blatt.daten, tp) : []
  return (
    <Wizard onClose={onClose} data-mode="staff" data-blatt-view={current} bar={bar} overlay={overlay}>
      {dokumentId ? (
        <SheetPdf
          key={dokumentId}
          dokumentId={dokumentId}
          sheet
          failedText={t('akte.sheetPdfError', { blatt: current })}
          onLoaded={(blob) => setPdf({ dokumentId, blob })}
        />
      ) : (
        <WizardPage>
          <Panel>
            <Title as="h1" size="lg">
              <ItemNumber dir="ltr">{current}</ItemNumber> {sheetTitle}
            </Title>
            {signed ? (
              <StatusLine tone="success" icon={<Icon name="i-check" />}>
                {blatt.unterschrieben_am
                  ? t('akte.signedAt', { zeitpunkt: formatZeitpunkt(blatt.unterschrieben_am) })
                  : t('akte.signed')}
              </StatusLine>
            ) : (
              <StatusLine>
                {t(status === 'offen' ? 'akte.notFilled' : sheet.kind === 'sign' ? 'akte.savedUnsigned' : 'akte.saved')}
              </StatusLine>
            )}
            {blatt.gegengezeichnet_am ? (
              <StatusLine tone="success" icon={<Icon name="i-signature" />}>
                {t('akte.countersignedAt', { zeitpunkt: formatZeitpunkt(blatt.gegengezeichnet_am) })}
              </StatusLine>
            ) : null}
            {countersignHint}
            {signed ? (
              <Callout tone="info" role="status">
                {t('akte.pdfPending')}
              </Callout>
            ) : null}
            {answers.length > 0 ? <DocumentFields fields={answers} /> : null}
          </Panel>
        </WizardPage>
      )}
    </Wizard>
  )
}
